import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Heart, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Donate = () => {
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  const presetAmounts = [10.00, 25.00, 50.00, 100.00];

  const handleDonate = async () => {
    const donationAmount = amount === 'custom' ? parseFloat(customAmount) : parseFloat(amount);
    
    if (!donationAmount || donationAmount <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }

    if (paymentMethod === 'paypal') {
      // Redirect to PayPal with specified amount
      const paypalEmail = 'darelldyal@gmail.com';
      const paypalUrl = `https://www.paypal.com/paypalme/darelldyal/${donationAmount}`;
      window.open(paypalUrl, '_blank');
      toast.success('Redirecting to PayPal...');
      return;
    }

    // Stripe payment flow
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount: donationAmount,
        origin_url: window.location.origin,
        donor_email: donorEmail || undefined,
        donor_name: donorName || undefined
      });
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to process donation. Please try again.');
      setLoading(false);
    }
  };

  // Check URL for session_id (returning from Stripe)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && !pollingSessionId) {
      setPollingSessionId(sessionId);
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
      
      if (data.payment_status === 'paid') {
        toast.success('Thank you for your generous donation! Your support helps us continue this ministry.');
        // Clear URL params
        window.history.replaceState({}, '', '/donate');
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/donate');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please contact us if you completed the donation.');
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />

      {/* Banner */}
      <section className="bg-brand-primary py-20" data-testid="donate-banner">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Heart className="h-16 w-16 text-brand-accent mx-auto mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-white mb-6">
            Support Our Mission
          </h1>
          <p className="font-body text-lg text-white/90">
            Your donation helps us continue providing free biblical resources to believers worldwide.
          </p>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          {/* Payment Method Selection */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-white">
              <button
                onClick={() => setPaymentMethod('stripe')}
                className={`px-6 py-2 rounded-md font-body font-medium text-sm transition-all duration-300 ${
                  paymentMethod === 'stripe'
                    ? 'bg-brand-accent text-white'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
                data-testid="payment-method-stripe"
              >
                Credit Card
              </button>
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`px-6 py-2 rounded-md font-body font-medium text-sm transition-all duration-300 ${
                  paymentMethod === 'paypal'
                    ? 'bg-brand-accent text-white'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
                data-testid="payment-method-paypal"
              >
                PayPal
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded shadow-sm p-8 md:p-12" data-testid="donation-form">
            {/* Impact Statement */}
            <div className="mb-8 p-6 bg-brand-accent/5 rounded border border-brand-accent/20">
              <p className="font-body text-sm text-brand-primary">
                <strong>Your Impact:</strong> Every donation helps us create, host, and share biblical resources with 
                people around the world. We are committed to keeping all resources completely free.
              </p>
            </div>

            {/* Preset Amounts */}
            <div className="mb-6">
              <label className="block font-body text-sm font-medium text-slate-700 mb-3">
                Select Amount
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setAmount(preset.toString())}
                    className={`p-4 border-2 rounded font-body font-medium transition-all duration-300 ${
                      amount === preset.toString()
                        ? 'border-brand-accent bg-brand-accent/5 text-brand-accent'
                        : 'border-slate-200 text-slate-700 hover:border-brand-accent/50'
                    }`}
                    data-testid={`preset-amount-${preset}`}
                  >
                    ${preset.toFixed(2)}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                Or Enter Custom Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 font-body text-slate-600">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setAmount('custom');
                  }}
                  className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                  placeholder="Enter amount"
                  data-testid="custom-amount-input"
                />
              </div>
            </div>

            {/* Donor Information (Optional) */}
            <div className="mb-6">
              <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                Name (Optional)
              </label>
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                placeholder="Your name"
                data-testid="donor-name-input"
              />
            </div>

            <div className="mb-8">
              <label className="block font-body text-sm font-medium text-slate-700 mb-2">
                Email (Optional)
              </label>
              <input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                placeholder="your@email.com"
                data-testid="donor-email-input"
              />
            </div>

            {/* Donate Button */}
            <button
              onClick={handleDonate}
              disabled={loading || (!amount && !customAmount)}
              className="w-full inline-flex items-center justify-center space-x-2 px-6 py-4 bg-brand-accent text-white font-body font-medium text-lg rounded hover:bg-brand-accent-hover transition-all duration-300 disabled:opacity-50"
              data-testid="proceed-to-donate-button"
            >
              <CreditCard className="h-5 w-5" />
              <span>
                {loading ? 'Processing...' : paymentMethod === 'stripe' ? 'Proceed to Payment' : 'Donate via PayPal'}
              </span>
            </button>

            <p className="mt-4 text-center font-body text-xs text-slate-600">
              {paymentMethod === 'stripe' 
                ? 'Secure payment powered by Stripe. All transactions are encrypted and secure.'
                : 'You will be redirected to PayPal to complete your donation securely.'
              }
            </p>

            {paymentMethod === 'paypal' && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-center font-body text-sm text-slate-700 mb-3">
                  Or donate directly to PayPal:
                </p>
                <div className="text-center">
                  <p className="font-body text-base font-medium text-brand-primary">
                    darelldyal@gmail.com
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Thank You Message */}
          <div className="mt-12 text-center">
            <p className="font-body text-base text-slate-700">
              Thank you for your generous support! Your donation is tax-deductible to the extent allowed by law.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Donate;